/**
 * Step5Comfort Component
 *
 * Fifth step of assessment wizard - collects comfort issues and energy cost
 */

import { useEffect } from 'react';

export default function Step5Comfort({ state, toggleIssue, setField, updateEnergyCostEstimate, errors }) {
  // Auto-update energy cost estimate when component mounts or relevant fields change
  useEffect(() => {
    updateEnergyCostEstimate();
  }, [updateEnergyCostEstimate]);

  const comfortIssues = [
    {
      key: 'hotCold',
      icon: '🌡️',
      label: 'Hot/cold spots or rooms that won\'t heat/cool',
      helper: 'Ask: Are certain rooms uncomfortable? Does temperature vary significantly between rooms?'
    },
    {
      key: 'humidity',
      icon: '💧',
      label: 'Excessive humidity or very dry air',
      helper: 'Ask: Does skin feel dry in winter? Does it feel sticky in summer? Do you notice condensation on windows?'
    },
    {
      key: 'runsConstantly',
      icon: '⏱️',
      label: 'System runs constantly or cycles frequently',
      helper: 'Ask: Does the system seem to never shut off? Do you hear it cycling on and off frequently?'
    },
    {
      key: 'highBills',
      icon: '⚡',
      label: 'Energy bills higher than expected',
      helper: 'Ask: Have you noticed your bills are higher than neighbors with similar homes? Have bills increased recently?'
    },
    {
      key: 'loud',
      icon: '🔊',
      label: 'Excessive noise during operation',
      helper: 'Ask: Do you hear rattling, banging, or squealing? Is it louder than you remember when new?'
    },
    {
      key: 'tempTrouble',
      icon: '🎯',
      label: 'Difficulty maintaining set temperature',
      helper: 'Ask: Does it struggle to reach your thermostat setting? Does it take a long time to cool/heat?'
    }
  ];

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '30px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}>
      <h2 style={{
        margin: '0 0 10px 0',
        color: '#2c3e50',
        fontSize: '24px'
      }}>
        Step 5: Comfort & Performance
      </h2>
      <p style={{
        margin: '0 0 25px 0',
        color: '#64748b',
        fontSize: '15px'
      }}>
        Select any comfort issues you've experienced with this system:
      </p>

      {/* Comfort Issues Checkboxes */}
      <div style={{ marginBottom: '30px' }}>
        {comfortIssues.map((issue) => (
          <label
            key={issue.key}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              padding: '15px',
              marginBottom: '12px',
              background: state.issues[issue.key] ? '#eff6ff' : '#f8f9fa',
              border: state.issues[issue.key] ? '2px solid #667eea' : '2px solid #e0e6ed',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              userSelect: 'none'
            }}
            onMouseEnter={(e) => {
              if (!state.issues[issue.key]) {
                e.currentTarget.style.borderColor = '#c7d2fe';
                e.currentTarget.style.background = '#fafbfc';
              }
            }}
            onMouseLeave={(e) => {
              if (!state.issues[issue.key]) {
                e.currentTarget.style.borderColor = '#e0e6ed';
                e.currentTarget.style.background = '#f8f9fa';
              }
            }}
          >
            <input
              type="checkbox"
              checked={state.issues[issue.key]}
              onChange={() => toggleIssue(issue.key)}
              style={{
                width: '20px',
                height: '20px',
                marginRight: '12px',
                cursor: 'pointer',
                marginTop: '2px',
                flexShrink: 0
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{
                fontWeight: '500',
                color: state.issues[issue.key] ? '#667eea' : '#2c3e50',
                fontSize: '15px',
                marginBottom: '4px'
              }}>
                <span style={{ marginRight: '8px' }}>{issue.icon}</span>
                {issue.label}
              </div>
              <div style={{
                fontSize: '13px',
                color: '#64748b',
                fontStyle: 'italic',
                lineHeight: '1.4'
              }}>
                {issue.helper}
              </div>
            </div>
          </label>
        ))}
      </div>

      {/* Energy Cost Estimate */}
      <div style={{
        padding: '20px',
        background: '#fffbeb',
        borderRadius: '8px',
        border: '2px solid #fbbf24',
        marginBottom: '15px'
      }}>
        <label style={{
          display: 'block',
          marginBottom: '12px',
          fontWeight: '600',
          color: '#2c3e50',
          fontSize: '16px'
        }}>
          Annual Energy Cost (Heating + Cooling)
        </label>
        <div style={{ position: 'relative' }}>
          <span style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#64748b',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            $
          </span>
          <input
            type="number"
            min="100"
            max="10000"
            step="50"
            value={state.annualEnergyCost}
            onChange={(e) => setField('annualEnergyCost', parseFloat(e.target.value) || 0)}
            style={{
              width: '100%',
              padding: '14px 14px 14px 35px',
              fontSize: '18px',
              fontWeight: '600',
              border: errors.annualEnergyCost ? '2px solid #ef4444' : '2px solid #f59e0b',
              borderRadius: '8px',
              outline: 'none',
              background: 'white'
            }}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => e.target.style.borderColor = errors.annualEnergyCost ? '#ef4444' : '#f59e0b'}
          />
        </div>
        {errors.annualEnergyCost && (
          <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '8px' }}>
            {errors.annualEnergyCost}
          </div>
        )}
        <div style={{
          marginTop: '12px',
          padding: '12px',
          background: 'white',
          borderRadius: '6px',
          fontSize: '13px',
          color: '#92400e'
        }}>
          <div style={{ marginBottom: '6px' }}>
            💡 <strong>This is an auto-calculated estimate based on:</strong>
          </div>
          <ul style={{ margin: '8px 0 0 20px', paddingLeft: '0' }}>
            <li>System age: {state.systemAge} years (affects efficiency)</li>
            <li>Square footage: {state.squareFootage.toLocaleString()} sq ft</li>
            <li>System type: {state.systemType}</li>
            <li>Duct condition: {state.ductCondition}</li>
            <li>Your climate zone and utility rates</li>
          </ul>
          <div style={{ marginTop: '10px', fontSize: '12px', fontStyle: 'italic' }}>
            <strong>Note:</strong> Comfort issues help determine our recommendation but don't affect this energy cost estimate, which is based on measurable physics and your actual utility rates.
          </div>
          <div style={{ marginTop: '8px', fontStyle: 'italic' }}>
            Adjust this value if it doesn't match your actual energy bills (check your utility statements for the most accurate estimate).
          </div>
        </div>
      </div>

      {/* Summary Info */}
      <div style={{
        padding: '15px',
        background: '#f0fdf4',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#059669'
      }}>
        ✓ Ready to analyze! Click "Calculate Results" below to see your personalized assessment.
      </div>
    </div>
  );
}
